import { Ref, useCallback, useState } from "react";

interface IsScrolled {
    left: boolean;
    top: boolean;
}

/**
 * React hook to track whether a DOM element is scrolled horizontally or vertically.
 *
 * @template T The type of the element to track (defaults to HTMLDivElement).
 * @returns An object containing:
 *   - isScrolled: An object with boolean values `{ left, top }` indicating if the element is scrolled left or top.
 *   - ref: A callback ref to assign to the scrollable element.
 *
 * @example
 * const { ref, isScrolled } = useIsScrolled<HTMLDivElement>();
 * return <div ref={ref}>...</div>;
 *
 * @remarks
 * This hook uses a callback ref instead of a ref object because, in scenarios where
 * the component that needs the ref is conditionally rendered (for example, when showing a loading state
 * with `isPending` from TanStack Query), the ref object (`ref.current`) can be undefined
 * when useEffect runs. The callback ref ensures you always receive the actual DOM element
 * when it is mounted, so you can safely attach and clean up event listeners.
 *
 * Note: This hook only tracks whether the element is scrolled left or top, and will only
 * cause a re-render when these boolean values change. This makes it more performant than
 * tracking the full scroll position, especially for use cases like sticky headers.
 */
export function useIsScrolled<T extends HTMLElement = HTMLDivElement>(): {
    isScrolled: IsScrolled;
    ref: Ref<T>;
    hasVerticalScrollbar: boolean;
} {
    const [isScrolledLeft, setIsScrolledLeft] = useState(false);
    const [isScrolledTop, setIsScrolledTop] = useState(false);
    const [hasVerticalScrollbar, setHasVerticalScrollbar] = useState(false);

    const ref = useCallback((el: T) => {
        if (!el) return;

        setIsScrolledLeft(el.scrollLeft > 0);
        setIsScrolledTop(el.scrollTop > 0);
        setHasVerticalScrollbar(el.scrollHeight > el.clientHeight);

        const handler = () => {
            setIsScrolledLeft(el.scrollLeft > 0);
            setIsScrolledTop(el.scrollTop > 0);
        };

        el.addEventListener("scroll", handler);

        return () => {
            setIsScrolledLeft(false);
            setIsScrolledTop(false);
            el.removeEventListener("scroll", handler);
        };
    }, []);

    return { ref, isScrolled: { left: isScrolledLeft, top: isScrolledTop }, hasVerticalScrollbar };
}
